import { useEffect, useState } from 'react';

import type { VideoTemplateConfigured } from './types';
import type { ShowcaseAccess } from '@src/types.ts/story';

/** The identity fields the access check reads — never the whole store. */
export interface TemplateEditIdentity {
  hasDatoContext: boolean;
  currentUserType: 'internal' | 'external';
  clerkUser?: { emailAddresses: { emailAddress: string }[] } | null;
}

interface AlbumAccessReader {
  findAllWithStoryAccess: (storyId: string) => Promise<ShowcaseAccess[]>;
}

interface TemplateEditAccess {
  /** Arbor staff and the Dato context: every template, including presets. */
  hasAccessToEditAll: boolean;
  /** A showcase admin editing one of their own customer templates. */
  hasAccessToEditThisTemplate: boolean;
}

/**
 * Who may edit the template currently open.
 *
 * The showcase-admin half is fetched and keyed on the identity and story; the
 * per-template half is **derived**, because the template list loads after this
 * check runs and the active template changes without it. Folding the template
 * into the fetched value froze it at whatever was loaded first — with an empty
 * list that resolves to "editable", so a preset stayed editable (PLAT-11219).
 */
export function useTemplateEditAccess({
  identity,
  albumRepository,
  storyId,
  activeTemplate,
}: {
  identity: TemplateEditIdentity;
  albumRepository?: AlbumAccessReader;
  storyId?: string;
  activeTemplate?: VideoTemplateConfigured;
}): TemplateEditAccess {
  const [hasAccessToEditAll, setHasAccessToEditAll] = useState(false);
  const [isShowcaseAdmin, setIsShowcaseAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (identity.hasDatoContext) {
        if (!cancelled) setHasAccessToEditAll(true);
        return;
      }

      if (identity.clerkUser) {
        const arborEmail = identity.clerkUser.emailAddresses.find(e =>
          e.emailAddress.endsWith('@tryarbor.com'),
        );
        if (arborEmail) {
          if (!cancelled) setHasAccessToEditAll(true);
          return;
        }

        const albums = await albumRepository?.findAllWithStoryAccess(
          storyId || '',
        );
        if (albums && !cancelled) {
          const isAdmin = albums.some(album =>
            album.adminList.some(admin =>
              identity.clerkUser?.emailAddresses.some(
                e => e.emailAddress === admin.email,
              ),
            ),
          );
          setIsShowcaseAdmin(isAdmin);
          return;
        }
      }
      if (!cancelled) setHasAccessToEditAll(false);
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [storyId, identity.currentUserType]);

  return {
    hasAccessToEditAll,
    hasAccessToEditThisTemplate:
      isShowcaseAdmin && !activeTemplate?.isGlobalPreset,
  };
}
